import { REACT_APP_API_URL } from 'helpers/conf';
import { PAYMENTS_REDIRECT_ROUTES } from 'helpers/constants';
import { buildStatusUrl } from 'helpers/payments';
import { sendDepositPaymentsAPIInitFailed } from 'modules/googleAnalytics';
import { useEffect, useState } from 'react';

// Singleton — deduplicates config calls for the same language.
let _configuredLanguage: string | null = null;
let _configPromise: Promise<void> | null = null;

const PAYMENTS_API_TIMEOUT_MS = 30_000;

const waitForPaymentsAPI = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.PaymentsAPI) return resolve();

    const timer = setTimeout(() => {
      clearInterval(interval);
      reject(new Error('PaymentsAPI script load timeout'));
    }, PAYMENTS_API_TIMEOUT_MS);

    const interval = setInterval(() => {
      if (window.PaymentsAPI) {
        clearInterval(interval);
        clearTimeout(timer);
        resolve();
      }
    }, 100);
  });

const initPaymentsAPI = (language: string): Promise<void> => {
  if (_configuredLanguage === language && _configPromise) return _configPromise;

  _configuredLanguage = language;
  _configPromise = waitForPaymentsAPI()
    .then(() => {
      const api = window.PaymentsAPI;
      if (!api) return Promise.reject(new Error('PaymentsAPI unavailable'));

      const statusUrl = buildStatusUrl();
      return api.config({
        language,
        serverUrl: REACT_APP_API_URL,
        cdnList: ['https://cdn.softswiss.net', 'https://cdn2.softswiss.net'],
        redirectRoutes: {
          successUrl: statusUrl(PAYMENTS_REDIRECT_ROUTES.SUCCESS),
          failureUrl: statusUrl(PAYMENTS_REDIRECT_ROUTES.FAILURE),
          pendingUrl: statusUrl(PAYMENTS_REDIRECT_ROUTES.PENDING),
          supportUrl: statusUrl(PAYMENTS_REDIRECT_ROUTES.SUPPORT),
        },
        contentPolicy: '3',
      });
    })
    .catch((err: Error) => {
      // Reset so the next call retries instead of reusing a rejected promise.
      _configuredLanguage = null;
      _configPromise = null;
      return Promise.reject(err);
    });

  return _configPromise;
};

// Pass i18n.language so locale changes trigger a clean re-init.
export const usePaymentsAPIReady = (language: string) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsReady(false);

    initPaymentsAPI(language)
      .then(() => {
        if (!cancelled) setIsReady(true);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        console.error('[PaymentsAPI] init failed:', err.message);
        sendDepositPaymentsAPIInitFailed({ error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  return isReady;
};
